import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  LinearProgress,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  WorkspacePremium,
  TrendingUp,
  Loyalty,
  FavoriteBorder,
  ShoppingCartOutlined,
  LocalOfferOutlined,
  StarRounded
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { getDashboardCrm } from '../../../controllers/company/crm/CrmController';



export default function ConsumerIntelligenceDashboard() {
    const history = useHistory();
    
    // 1. ESTADOS DEL COMPONENTE
    const [dashboardData, setDashboardData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // 2. EFECTO PARA CONSUMIR LA API AL MONTAR LA PANTALLA
    useEffect(() => {
        let isMounted = true; // Para evitar actualizaciones de estado si el componente se desmonta

        const fetchData = async () => {
            setIsLoading(true);
            const data = await getDashboardCrm();
            
            if (isMounted) {
                setDashboardData(data);
                setIsLoading(false);
            }
        };

        fetchData();

        return () => { isMounted = false; };
    }, []);

    const redirect = (route) => {
        history.push(route);
    }

    // 3. PANTALLA DE CARGA
    if (isLoading || !dashboardData) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Stack alignItems="center" spacing={2}>
                    <CircularProgress size={48} />
                    <Typography color="text.secondary">Analizando métricas del negocio...</Typography>
                </Stack>
            </Box>
        );
    }

    // 4. MAPEO DINÁMICO DE KPIS 
    // Usamos la data real devuelta por el backend para armar las tarjetas superiores
    const kpisList = [
        { title: 'Consumidores Únicos', value: dashboardData.kpis.total_unique, sub: 'En tu base de datos', icon: <FavoriteBorder color="error" />, bg: '#fef2f2' },
        { title: 'Gasto Promedio (Ticket)', value: dashboardData.kpis.avg_ticket, sub: 'Promedio global histórico', icon: <TrendingUp color="success" />, bg: '#f0fdf4' },
        { title: 'Tasa de Retención', value: dashboardData.kpis.retention_rate, sub: 'Han comprado más de 1 vez', icon: <Loyalty color="primary" />, bg: '#eff6ff' },
    ];

    return (
        <Box sx={{ minHeight: '100vh', py: 4 }}>
            <Container maxWidth="xl">
                
                {/* Header enfocado en la salud del negocio */}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={4} spacing={2}>
                    <Box>
                        <Typography variant="h4" fontWeight="800" color="text.primary" letterSpacing="-0.5px">
                        Inteligencia de Consumidores
                        </Typography>
                        <Typography variant="body1" color="text.secondary" mt={0.5}>
                        Conoce quién te compra, cómo interactúan y qué los hace volver.
                        </Typography>
                    </Box>
                    <Box> 
                        <Button
                            onClick={()=>redirect('/crm/client-list')}
                            variant="contained"
                            disableElevation
                        >
                            Ver todos los clientes
                        </Button>
                    </Box>
                </Stack>

                {/* KPIs de Valor del Cliente (Renderizado Dinámico) */}
                <Grid container spacing={3} mb={4}>
                {kpisList.map((kpi, index) => (
                    <Grid item xs={12} md={4} key={index}>
                    <Card variant="outlined" sx={{ borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <Avatar sx={{ bgcolor: kpi.bg, width: 48, height: 48 }}>
                            {kpi.icon}
                            </Avatar>
                            <Box>
                            <Typography variant="h5" fontWeight="800" color="text.primary">
                                {kpi.value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight="600">
                                {kpi.title}
                            </Typography>
                            </Box>
                        </Stack>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                            {kpi.sub}
                        </Typography>
                        </CardContent>
                    </Card>
                    </Grid>
                ))}
                </Grid>

                <Grid container spacing={4}>
                
                {/* Columna Izquierda: Segmentación y Lealtad */}
                <Grid item xs={12} md={7}>
                    <Stack spacing={4}>
                    
                    {/* Top Consumidores (Iteramos los del backend) */}
                    <Card variant="outlined" sx={{ borderRadius: 4, border: '1px solid #e2e8f0' }}>
                        <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                            Top Consumidores (VIP)
                        </Typography>
                        <Button size="small" sx={{ textTransform: 'none', fontWeight: 600 }}>Ver todos</Button>
                        </Box>
                        
                        {dashboardData.top_consumers.length === 0 ? (
                             <Box sx={{ p: 4, textAlign: 'center' }}>
                                 <Typography color="text.secondary">Aún no hay suficientes ventas para calcular los clientes VIP.</Typography>
                             </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                            {dashboardData.top_consumers.map((user, index) => (
                                <React.Fragment key={user.id}>
                                <ListItem sx={{ p: 3 }}>
                                    <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 'bold' }}>{user.avatar}</Avatar>
                                    </ListItemAvatar>
                                    <ListItemText 
                                    primary={<Typography fontWeight="bold">{user.name}</Typography>}
                                    secondary={`${user.visits} visitas acumuladas`}
                                    />
                                    <Stack alignItems="flex-end">
                                    <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                                        {user.spent}
                                    </Typography>
                                    <Chip 
                                        label={`Nivel ${user.tier}`} 
                                        size="small" 
                                        icon={<WorkspacePremium fontSize="small" />}
                                        sx={{ mt: 0.5, bgcolor: user.tier === 'Diamante' ? '#fdf2f8' : '#fef3c7', color: user.tier === 'Diamante' ? '#be185d' : '#b45309', fontWeight: 600 }} 
                                    />
                                    </Stack>
                                </ListItem>
                                {index < dashboardData.top_consumers.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            ))}
                            </List>
                        )}
                    </Card>

                    {/* Distribución de la Base de Clientes (Mapeamos la data de segmentación) */}
                    <Card variant="outlined" sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 3 }}>
                        <Typography variant="h6" fontWeight="bold" color="text.primary" mb={3}>
                        Salud de la Audiencia (Segmentación)
                        </Typography>
                        
                        <Stack spacing={3}>
                        <Box>
                            <Stack direction="row" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" fontWeight="600">Clientes Recurrentes (Han comprado +2 veces)</Typography>
                            <Typography variant="body2" fontWeight="bold">{dashboardData.segmentation.recurrent_pct}%</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={dashboardData.segmentation.recurrent_pct} sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }} />
                        </Box>
                        
                        <Box>
                            <Stack direction="row" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" fontWeight="600">Nuevos Clientes (Últimos 30 días)</Typography>
                            <Typography variant="body2" fontWeight="bold">{dashboardData.segmentation.new_pct}%</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={dashboardData.segmentation.new_pct} sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: 'secondary.main' } }} />
                        </Box>

                        <Box>
                            <Stack direction="row" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" fontWeight="600">En Riesgo (Sin compras en 60 días)</Typography>
                            <Typography variant="body2" fontWeight="bold" color="error.main">{dashboardData.segmentation.risk_pct}%</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={dashboardData.segmentation.risk_pct} sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: 'error.main' } }} />
                        </Box>
                        </Stack>
                    </Card>

                    </Stack>
                </Grid>

                {/* Columna Derecha: Pulso en Vivo */}
                <Grid item xs={12} md={5}>
                    <Card variant="outlined" sx={{ borderRadius: 4, border: '1px solid #e2e8f0', height: '100%', bgcolor: '#ffffff' }}>
                    <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                        Pulso del Consumidor
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                        Interacciones en tiempo real en tu ecosistema
                        </Typography>
                    </Box>
                    
                    {dashboardData.live_pulse.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">Aún no hay actividad reciente registrada.</Typography>
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {dashboardData.live_pulse.map((event, index) => (
                            <React.Fragment key={event.id}>
                                <ListItem alignItems="flex-start" sx={{ p: 3 }}>
                                <ListItemAvatar>
                                    <Avatar sx={{ 
                                    bgcolor: event.type === 'promo' ? '#fef3c7' : event.type === 'review' ? '#f0fdf4' : '#eff6ff',
                                    color: event.type === 'promo' ? '#d97706' : event.type === 'review' ? '#16a34a' : '#2563eb'
                                    }}>
                                    {event.type === 'promo' ? <LocalOfferOutlined /> : event.type === 'review' ? <StarRounded /> : <ShoppingCartOutlined />}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                        {event.action}
                                    </Typography>
                                    }
                                    secondary={
                                    <Stack direction="row" justifyContent="space-between" mt={0.5}>
                                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                                        {event.customer}
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled">
                                        {event.time}
                                        </Typography>
                                    </Stack>
                                    }
                                />
                                </ListItem>
                                {index < dashboardData.live_pulse.length - 1 && <Divider component="li" variant="inset" />}
                            </React.Fragment>
                            ))}
                        </List>
                    )}
                    
                    <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', mt: 'auto' }}>
                        <Button variant="text" size="small" sx={{ fontWeight: 600, textTransform: 'none' }}>
                        Ver mapa de calor de hoy
                        </Button>
                    </Box>
                    </Card>
                </Grid>
                
                </Grid>
            </Container>
        </Box>
    );
}